/**
 * GlobalTradingHeader.tsx — MER Algo Footprints v9.2.0
 *
 * Phase 2: Persistent Global Trading Header (HUD).
 *
 * Total height: 188px
 *   Row 1 — Brand Bar          (78px)  — HeaderBrandBlock
 *   Row 2 — Decision Context    (68px)  — Instrument + Metric tiles
 *   Row 3 — System Strip        (42px)  — HeaderSystemStrip
 *
 * Mounted once at app shell level so the same context is visible on every
 * route. Calls useGlobalTradingContext() ONCE and threads the VM into the
 * three rows. NO new business logic.
 *
 * The component is fixed-positioned and sits above page content. AppShell
 * is responsible for adding paddingTop to its outlet area to compensate.
 */

import React from 'react';
import { useGlobalTradingContext } from '../../hooks/useGlobalTradingContext';
import { HeaderBrandBlock } from './HeaderBrandBlock';
import { HeaderInstrumentBlock } from './HeaderInstrumentBlock';
import { HeaderMetricTile } from './HeaderMetricTile';
import { HeaderStatusPill } from './HeaderStatusPill';
import { HeaderSystemStrip } from './HeaderSystemStrip';
import '../../styles/globalTradingHeader.css';

// ─── Helpers — formatting only, no business logic ────────────────────────────

function formatNumber(n: number | null, opts?: { decimals?: number; suffix?: string }): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const d = opts?.decimals ?? 2;
  const s = opts?.suffix ?? '';
  return `${n.toFixed(d)}${s}`;
}

function formatPercent(n: number | null, decimals = 0): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(decimals)}%`;
}

function regimeTone(label: string | null):
  'green' | 'red' | 'yellow' | 'purple' | 'muted' {
  if (!label) return 'muted';
  const l = label.toLowerCase();
  if (l.includes('breakout') || l.includes('trend')) return 'green';
  if (l.includes('reversion'))                       return 'yellow';
  if (l.includes('no'))                              return 'muted';
  return 'plain' as 'muted';
}

function postureTone(p: string | null):
  'green' | 'red' | 'yellow' | 'muted' {
  if (!p) return 'muted';
  const v = p.toLowerCase();
  if (v.includes('bull')) return 'green';
  if (v.includes('bear')) return 'red';
  if (v.includes('neutral') || v.includes('mixed')) return 'yellow';
  return 'muted';
}

function gammaTone(regime: string | null):
  'green' | 'red' | 'yellow' | 'muted' {
  if (!regime) return 'muted';
  const r = regime.toLowerCase();
  if (r.includes('positive')) return 'green';
  if (r.includes('negative')) return 'red';
  if (r.includes('neutral') || r.includes('flat')) return 'yellow';
  return 'muted';
}

function emptyText(loading: boolean): string {
  return loading ? 'Loading…' : 'No data';
}

// ─── Main component ──────────────────────────────────────────────────────────

export function GlobalTradingHeader() {
  const vm = useGlobalTradingContext();

  // Build display values once per render. Spec Step 9: never blank.
  const empty = emptyText(vm.loading);

  // Row 2 tile values (ReactNodes — pills or strings)
  const regimeNode = vm.regime
    ? <HeaderStatusPill tone={regimeTone(vm.regime)} size="md" label={vm.regime} />
    : <span style={{ color: 'var(--mer-muted)' }}>{empty}</span>;

  const postureNode = vm.posture
    ? <HeaderStatusPill tone={postureTone(vm.posture)} size="md" label={vm.posture} dot />
    : <span style={{ color: 'var(--mer-muted)' }}>{empty}</span>;

  const confidenceNode =
    vm.confidence != null
      ? formatPercent(vm.confidence)
      : <span style={{ color: 'var(--mer-muted)' }}>{empty}</span>;

  const rvolNode =
    vm.rvol != null
      ? formatNumber(vm.rvol, { decimals: 2, suffix: '×' })
      : <span style={{ color: 'var(--mer-muted)' }}>{empty}</span>;

  const keyLevelValue =
    vm.keyLevel
      ? `${vm.keyLevel.label} @ ${vm.keyLevel.price.toFixed(2)}`
      : <span style={{ color: 'var(--mer-muted)' }}>{empty}</span>;

  const gammaValue =
    vm.gamma.regime
      ? <HeaderStatusPill tone={gammaTone(vm.gamma.regime)} size="md" label={vm.gamma.regime} />
      : <span style={{ color: 'var(--mer-muted)' }}>{empty}</span>;

  const gammaNote = vm.gamma.flip != null
    ? `flip ${vm.gamma.flip.toFixed(2)}`
    : null;

  // Clock + market indicator (rightmost on Row 2)
  const clockText = new Date(vm.currentTime).toLocaleTimeString('en-US', {
    hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const marketLabel =
    vm.marketStatus === 'open'     ? 'OPEN'
    : vm.marketStatus === 'extended' ? 'EXT'
    : 'CLOSED';
  const marketTone =
    vm.marketStatus === 'open'     ? 'green' as const
    : vm.marketStatus === 'extended' ? 'yellow' as const
    : 'muted' as const;

  return (
    <header
      className="mer-global-header"
      role="banner"
      aria-label="MER Algo Footprints global trading header"
      style={{
        position: 'fixed',
        top: 0,
        left: 52,                 // matches AppShell left rail width
        right: 0,
        height: 188,
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--mer-bg-mid)',
        borderBottom: '1px solid var(--mer-border)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Row 1 — Brand Bar.
          appVersion intentionally not passed — HeaderBrandBlock defaults to
          the canonical APP_VERSION import (commit 3.9 follow-up to 3.8). */}
      <HeaderBrandBlock />

      {/* Row 2 — Decision Context */}
      <div
        className="mer-decision-row"
        style={{
          background: 'var(--mer-bg-mid)',
          height: 68,
          display: 'flex',
          alignItems: 'stretch',
          overflowX: 'auto',
        }}
      >
        <HeaderInstrumentBlock
          symbol={vm.symbol}
          price={vm.price}
          changePct={vm.changePct}
          loading={vm.loading}
          error={vm.error}
        />

        <HeaderMetricTile
          label="Regime"
          value={regimeNode}
          minWidth={140}
        />

        <HeaderMetricTile
          label="Posture"
          value={postureNode}
          minWidth={140}
        />

        <HeaderMetricTile
          label="Confidence"
          value={confidenceNode}
          minWidth={100}
        />

        <HeaderMetricTile
          label="RVOL"
          value={rvolNode}
          minWidth={100}
        />

        <HeaderMetricTile
          label="Key Level"
          value={keyLevelValue}
          note={vm.keyLevelNote}
          className="mer-tile-key-level"
          minWidth={200}
        />

        <HeaderMetricTile
          label="Gamma"
          value={gammaValue}
          note={gammaNote}
          minWidth={140}
        />

        {/* Clock + market — pushed right */}
        <div style={{ flex: 1, minWidth: 8 }} />

        <HeaderMetricTile
          label="Clock"
          value={clockText}
          note={<HeaderStatusPill tone={marketTone} size="sm" label={marketLabel} dot />}
          align="center"
          minWidth={120}
          className="mer-tile-clock"
        />
      </div>

      {/* Row 3 — System Strip */}
      <HeaderSystemStrip vm={vm} />
    </header>
  );
}
